import { useState, useEffect, useRef, useMemo } from "react";
import { Calculator, DollarSign, Percent, Gift, Info } from "lucide-react";

import LeadGateModal from "../Modals/LeadGateModal.jsx";
import SuccessModal from "../Modals/SuccessModal.jsx";
import ConfirmSendResultsModal from "../Modals/ConfirmSendResultsModal.jsx";
import UpdateEmailModal from "../Modals/UpdateEmailModal.jsx";

import { detectToolsOrigin } from "../../../utils/helper.js";
import axios from "axios";

import "../calculator.scss";

// ====== YEAR CONFIG (THIS IS WHERE YOU EDIT PER YEAR) ======
const YEAR_CONFIG = {
  2025: {
    defaultTaxRate: "0.22",
    taxRateOptions: [
      { value: "0.06", label: "6%" },
      { value: "0.12", label: "12%" },
      { value: "0.22", label: "22%" },
      { value: "0.24", label: "24%" },
      { value: "0.32", label: "32%" },
      { value: "0.35", label: "35%" },
      { value: "0.37", label: "37%" },
    ],
    supplemental: {
      baseRate: 0.22,
      excessRate: 0.37,
      threshold: 1_000_000,
    },
    disclaimer:
      "* This calculator provides estimates for federal tax withholding only. Actual withholding may vary based on your employer's payroll system, state taxes, and other factors. The percentage method uses the current 22% flat rate for supplemental income. Consult a tax professional for personalized advice.",
  },

  2026: {
    defaultTaxRate: "0.22",
    taxRateOptions: [
      { value: "0.06", label: "6%" },
      { value: "0.12", label: "12%" },
      { value: "0.22", label: "22%" },
      { value: "0.24", label: "24%" },
      { value: "0.32", label: "32%" },
      { value: "0.35", label: "35%" },
      { value: "0.37", label: "37%" },
    ],
    supplemental: {
      baseRate: 0.22,
      excessRate: 0.37,
      threshold: 1_000_000,
    },
    disclaimer:
      "* This calculator provides estimates for federal tax withholding only. Actual withholding may vary based on your employer's payroll system, state taxes, and other factors. The percentage method uses the current 22% flat rate for supplemental income. Consult a tax professional for personalized advice.",
  },
};

// ====== SHARED HELPERS (unchanged logic) ======
const toNumber = (v) => {
  if (v === null || v === undefined) return 0;

  if (typeof v === "number") return Number.isFinite(v) ? v : 0;

  if (typeof v === "object") {
    if ("value" in v) return toNumber(v.value);
    return 0;
  }

  const s = String(v).trim().replace(/,/g, "").replace(/%$/, "");
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
};

const toRate = (v) => {
  const n = toNumber(v);
  return n > 1 ? n / 100 : n;
};

const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);

const computeAggregateBonusWithholding = ({ lastPaycheck, bonus, rate }) => {
  const taxRegular = lastPaycheck * rate;
  const taxCombined = (lastPaycheck + bonus) * rate;
  const delta = Math.max(0, taxCombined - taxRegular);
  return round2(delta);
};

const computeSupplementalWithholding = ({ bonus, supplemental }) => {
  const { baseRate, excessRate, threshold } = supplemental;

  const basePortion = Math.min(bonus, threshold);
  const excessPortion = Math.max(bonus - threshold, 0);

  const baseTax = basePortion * baseRate;
  const excessTax = excessPortion * excessRate;
  const total = baseTax + excessTax;

  return {
    total: round2(total),
    basePortion: round2(basePortion),
    excessPortion: round2(excessPortion),
    baseTax: round2(baseTax),
    excessTax: round2(excessTax),
    hasExcess: excessPortion > 0,
  };
};

  // ---------- PROXY (same pattern as Federal) ----------
  const PROXY_URL =
    window.__UK_PROXY_URL__ ||
    `${detectToolsOrigin()}/.netlify/functions/api-proxy`;

// ====== MAIN COMPONENT ======
function BonusTaxCalculator({
  year = 2025,
  calculatorName = "Bonus Tax Calculator",
  // keep prop for compatibility, but we’ll send exactly like Federal (via proxy)
  endpoint = PROXY_URL,
}) {
  const config = YEAR_CONFIG[year] || YEAR_CONFIG[2025];


  const [lastPaycheck, setLastPaycheck] = useState("");
  const [bonusAmount, setBonusAmount] = useState("");
  const [taxRate, setTaxRate] = useState(config.defaultTaxRate);

  // ✅ results should ONLY appear after clicking View Results (but UI container always visible)
  const [results, setResults] = useState(null);
  const [hasCalculated, setHasCalculated] = useState(false);

  const resultsRef = useRef(null);

  // ✅ keep latest structured payload for emailing even if state changes
  const latestPayloadRef = useRef({ inputs_json: null, results_json: null });

  // ---------- Gating storage helpers (24h, shared across calculators) ----------
  const UNK_GATE_KEY = "unk_calc_unlock_v1";
  const UNK_GATE_TTL_MS = 24 * 60 * 60 * 1000;

  const readUnlock = () => {
    try {
      const raw = localStorage.getItem(UNK_GATE_KEY);
      if (!raw) return { unlocked: false, email: "" };
      const parsed = JSON.parse(raw);
      const expiresAt = Number(parsed?.expiresAt || 0);
      const email = String(parsed?.email || "");
      if (!expiresAt || Date.now() > expiresAt) {
        localStorage.removeItem(UNK_GATE_KEY);
        return { unlocked: false, email: "" };
      }
      return { unlocked: true, email };
    } catch {
      return { unlocked: false, email: "" };
    }
  };

  const writeUnlock = ({ email }) => {
    const payload = {
      expiresAt: Date.now() + UNK_GATE_TTL_MS,
      email: String(email || ""),
    };
    localStorage.setItem(UNK_GATE_KEY, JSON.stringify(payload));
  };

  const pageUrl = typeof window !== "undefined" ? window.location.href : "";

  // gate state
  const [unlocked, setUnlocked] = useState(false);
  const [storedEmail, setStoredEmail] = useState("");

  // modals
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showUpdateEmailModal, setShowUpdateEmailModal] = useState(false);

  const [sendingResults, setSendingResults] = useState(false);
    const params = useMemo(() => new URLSearchParams(window.location.search), []);
  
    const qa = useMemo(
    () => (params.has("qa_mode")),
    [params]
  );

  useEffect(() => {
    const gate = readUnlock();
    setUnlocked(gate.unlocked);
    setStoredEmail(gate.email || "");
  }, []);

  // ✅ UX: if they change inputs after calculating, hide results until they click again
  useEffect(() => {
    setHasCalculated(false);
    setResults(null);
    latestPayloadRef.current = { inputs_json: null, results_json: null };
  }, [lastPaycheck, bonusAmount, taxRate, year]);

  const computeResults = () => {
    const paycheckNum = Math.max(0, toNumber(lastPaycheck));
    const bonusNum = Math.max(0, toNumber(bonusAmount));
    const rateNum = Math.max(0, toRate(taxRate));

    if (
      !Number.isFinite(paycheckNum) ||
      !Number.isFinite(bonusNum) ||
      !Number.isFinite(rateNum)
    )
      return null;

    if (bonusNum <= 0) return null;

    const aggregateBonusWithheld = computeAggregateBonusWithholding({
      lastPaycheck: paycheckNum,
      bonus: bonusNum,
      rate: rateNum,
    });

    const totalWithholdingOnCombined = round2((paycheckNum + bonusNum) * rateNum);

    const supplemental = computeSupplementalWithholding({
      bonus: bonusNum,
      supplemental: config.supplemental,
    });

    const inputs_json = {
      lastPaycheck: paycheckNum,
      bonusAmount: bonusNum,
      taxRate: rateNum,
      taxRateLabel:
        config.taxRateOptions.find((o) => String(o.value) === String(taxRate))
          ?.label || `${Math.round(rateNum * 100)}%`,
      year: Number(year),
      calculatorName,
    };

const results_json = {
  totalPaycheckAndBonus: round2(paycheckNum + bonusNum),
  aggregateBonusWithheld,
  totalWithholdingOnCombined,
  percentageBonusWithheld: supplemental.total,
};
const pdf_notes = {
  disclaimer: config.disclaimer,
  methods: {
    aggregate: "Used when your bonus is paid with your regular paycheck. Taxes are calculated at your normal withholding rate on the combined amount.",
    percentage: `Used when your bonus is paid separately. A flat ${Math.round(config.supplemental.baseRate * 100)}% federal withholding rate is applied to the bonus amount (with a higher rate above the threshold).`,
  },
  supplemental_rates: {
    base_rate_percent: Math.round(config.supplemental.baseRate * 100),
    excess_rate_percent: Math.round(config.supplemental.excessRate * 100),
    threshold: config.supplemental.threshold,
  },
  footer: {
    cta_title: "Want a personalized tax strategy? Speak with an Uncle Kam expert.",
    cta_subtitle: "Visit us at unclekam.com",
  },
};



    return { inputs_json, results_json, pdf_notes };
  };

  const scrollToResults = () => {
    const el = resultsRef.current;
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const openGateFlow = () => {
    if (unlocked) {
      setShowConfirmModal(true);
      return;
    }
    setShowLeadModal(true);
  };

  const handleLeadSuccess = ({ email }) => {
    writeUnlock({ email });
    setUnlocked(true);
    setStoredEmail(email || "");
    setShowLeadModal(false);
    setShowSuccessModal(true);
  };

  const handleSuccessViewResults = () => {
    setShowSuccessModal(false);
    scrollToResults();
  };

  // ✅ UPDATED: SEND REQUESTS EXACTLY LIKE FEDERAL (axios -> PROXY_URL with {path, method, raw, data})
  const sendResultsToEmail = async (emailToSend) => {
    const payload = latestPayloadRef.current;
    if (!payload?.inputs_json || !payload?.results_json) return null;

    setSendingResults(true);

    try {
      const resp = await axios.post(
        PROXY_URL,
        {
          path: "/public/send-results",
          method: "POST",
          raw: true,
          qa_mode: qa,
          data: {
            action: "send_results_email",
            email: emailToSend,
            calculator_name: calculatorName,
            calculator_year: String(year),
            page_url: pageUrl,
            timestamp_utc: new Date().toISOString(),
            inputs_json: payload.inputs_json,
            results_json: payload.results_json,
            pdf_notes: payload.pdf_notes,
            schema_version: 1,
          },
        },
        {
          headers: { "Content-Type": "application/json" },
          timeout: 30000,
        }
      );

      if (resp?.data?.success === false) {
        throw new Error(resp?.data?.message || "Failed to send results email");
      }

      setShowConfirmModal(false);
      setShowUpdateEmailModal(false);
      scrollToResults();

      return resp.data;
    } catch (err) {
      console.error("sendResultsToEmail error:", err);
      return null;
    } finally {
      setSendingResults(false);
    }
  };

  const handleConfirmSend = async () => {
    if (!storedEmail) {
      setShowConfirmModal(false);
      setShowUpdateEmailModal(true);
      return;
    }
    await sendResultsToEmail(storedEmail);
  };

  const handleChangeEmail = () => {
    setShowConfirmModal(false);
    setShowUpdateEmailModal(true);
  };

  const handleUpdateEmailAndSend = async (newEmail) => {
    writeUnlock({ email: newEmail });
    setStoredEmail(newEmail);
    await sendResultsToEmail(newEmail);
  };

  const handleViewResultsClick = () => {
    const computed = computeResults();
    if (!computed) {
      setResults(null);
      setHasCalculated(false);
      latestPayloadRef.current = { inputs_json: null, results_json: null };
      return;
    }

    setResults(computed.results_json);
    setHasCalculated(true);
    latestPayloadRef.current = computed;

    openGateFlow();
  };

  const canCalculate = toNumber(bonusAmount) > 0;

  return (
    <>
      <div className="min-vh-100 bg-gradient">
        <div className="container py-5">
          <div className="row g-4">
            <div className="col-lg-6">
              <div className="card shadow">
                <div className="card-header d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-2">
                  <div>
                    <h5 className="card-title d-flex align-items-center gap-2">
                      <i className="bi bi-gift text-primary"></i>
                      Bonus Information
                    </h5>
                    <small className="text-muted">
                      Enter your paycheck and bonus details to calculate tax
                      withholding
                    </small>
                  </div>
                  <span className="badge bg-primary-subtle text-primary fw-semibold">
                    Tax Year {year}
                  </span>
                </div>

                <div className="card-body">
                  <div className="mb-4">
                    <label
                      htmlFor="lastPaycheck"
                      className="form-label fw-semibold"
                    >
                      Amount of Last Paycheck
                    </label>
                    <div className="position-relative">
                      <i className="bi bi-currency-dollar position-absolute top-50 translate-middle-y start-2 ps-2 text-muted"></i>
                      <input
                        type="number"
                        className="form-control ps-2 border-success border-2"
                        id="lastPaycheck"
                        placeholder="Enter your last paycheck amount"
                        value={lastPaycheck}
                        onChange={(e) => setLastPaycheck(e.target.value)}
                      />
                    </div>
                    <div className="form-text">
                      Used for aggregate method calculation
                    </div>
                  </div>

                  <div className="mb-4">
                    <label
                      htmlFor="bonusAmount"
                      className="form-label fw-semibold"
                    >
                      Amount of Bonus
                    </label>
                    <div className="position-relative">
                      <i className="bi bi-currency-dollar position-absolute top-50 translate-middle-y start-2 ps-2 text-muted"></i>
                      <input
                        type="number"
                        className="form-control ps-2 border-warning border-2"
                        id="bonusAmount"
                        placeholder="Enter your bonus amount"
                        value={bonusAmount}
                        onChange={(e) => setBonusAmount(e.target.value)}
                      />
                    </div>
                    <div className="form-text">
                      The bonus amount you will receive
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="form-label fw-semibold">
                      Annual Income Tax Rate
                    </label>
                    <select
                      className="form-select border-info border-2"
                      value={taxRate}
                      onChange={(e) => setTaxRate(e.target.value)}
                    >
                      <option value="">Select your tax rate</option>
                      {config.taxRateOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <div className="form-text">
                      Your current withholding rate (used for aggregate method)
                    </div>
                  </div>

                  {/* ✅ this box still appears ONLY after calculate click */}
                  {hasCalculated && results && (
                    <div className="border p-3 rounded">
                      <label className="form-label fw-semibold">
                        Total Paycheck and Bonus
                      </label>
                      <div className="fs-4 fw-bold text-primary">
                        {formatCurrency(results.totalPaycheckAndBonus)}
                      </div>
                    </div>
                  )}

                  <button
                    className="btn w-100 mt-3"
                    onClick={handleViewResultsClick}
                    style={{
                      background: "#0f8a4c",
                      borderColor: "#0f8a4c",
                      color: "white",
                      fontWeight: 700,
                    }}
                    disabled={!canCalculate}
                  >
                    View Results
                  </button>

                  {!canCalculate && (
                    <div className="mt-2 small text-muted">
                      Add a bonus amount to enable results.
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ✅ ALWAYS render the Results column (blurred/locked by default) */}
            <div className="col-lg-6" ref={resultsRef}>
              <div className="card shadow">
                <div className="card-header">
                  <h5 className="card-title d-flex align-items-center gap-2">
                    <i className="bi bi-percent text-primary"></i>
                    Tax Withholding Calculation
                  </h5>
                  <small className="text-muted">
                    Comparison of both calculation methods
                  </small>
                </div>

                <div className="card-body">
                  <div
                    className="unk-results-wrap rounded-4"
                    style={{
                      background: !unlocked
                        ? "linear-gradient(180deg, rgba(255,255,255,0.92), rgba(255,255,255,0.92))"
                        : "transparent",
                    }}
                  >
                    {!unlocked && <div className="unk-bg-blur" />}

                    <div style={{ position: "relative", zIndex: 1, width: "100%" }}>
                      <div className={!unlocked ? "unk-results-blur" : ""}>
                        {hasCalculated && results ? (
                          <>
                            <div className="row g-4">
                              <div className="col-md-6">
                                <div className="p-3 bg-success text-white rounded">
                                  <div className="d-flex align-items-center gap-2 mb-2">
                                    <i className="bi bi-calculator"></i>
                                    <span className="fw-semibold">
                                      Aggregate Method
                                    </span>
                                  </div>
                                  <div className="fs-3 fw-bold">
                                    {formatCurrency(results.aggregateBonusWithheld)}
                                  </div>
                                  <div className="small">
                                    Bonus withheld (paid with regular paycheck)
                                  </div>
                                  <div className="small text-white-50 mt-1">
                                    Total withholding on combined check:{" "}
                                    <span className="fw-semibold">
                                      {formatCurrency(
                                        results.totalWithholdingOnCombined
                                      )}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              <div className="col-md-6">
                                <div className="p-3 bg-danger text-white rounded">
                                  <div className="d-flex align-items-center gap-2 mb-2">
                                    <i className="bi bi-percent"></i>
                                    <span className="fw-semibold">
                                      Percentage Method
                                    </span>
                                  </div>
                                  <div className="fs-3 fw-bold">
                                    {formatCurrency(results.percentageBonusWithheld)}
                                  </div>
                                  <div className="small">
                                    Supplemental wage rules
                                  </div>

                                  {results.percentageBreakdown?.hasExcess ? (
                                    <div className="small text-white-75 mt-2">
                                      {Math.round(
                                        config.supplemental.baseRate * 100
                                      )}
                                      % of first{" "}
                                      {formatCurrency(
                                        results.percentageBreakdown.basePortion
                                      )}{" "}
                                      ={" "}
                                      <span className="fw-semibold">
                                        {formatCurrency(
                                          results.percentageBreakdown.baseTax
                                        )}
                                      </span>
                                      <br />
                                      {Math.round(
                                        config.supplemental.excessRate * 100
                                      )}
                                      % of excess{" "}
                                      {formatCurrency(
                                        results.percentageBreakdown.excessPortion
                                      )}{" "}
                                      ={" "}
                                      <span className="fw-semibold">
                                        {formatCurrency(
                                          results.percentageBreakdown.excessTax
                                        )}
                                      </span>
                                    </div>
                                  ) : (
                                    <div className="small text-white-75 mt-2">
                                      {Math.round(
                                        config.supplemental.baseRate * 100
                                      )}
                                      % flat rate applies (bonus ≤{" "}
                                      {formatCurrency(config.supplemental.threshold)}).
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className="mt-4 p-4 border rounded">
                              <div className="d-flex align-items-center gap-2 mb-3">
                                <i className="bi bi-info-circle text-primary"></i>
                                <h6 className="mb-0 fw-semibold">
                                  Understanding the Methods
                                </h6>
                              </div>
                              <div className="row g-3">
                                <div className="col-md-6">
                                  <h6 className="fw-semibold text-success">
                                    Aggregate Method
                                  </h6>
                                  <p className="text-muted small">
                                    Used when your bonus is paid with your regular paycheck. Taxes are calculated at your normal
                                    withholding rate on the combined amount.
                                  </p>
                                </div>
                                <div className="col-md-6">
                                  <h6 className="fw-semibold text-danger">
                                    Percentage Method
                                  </h6>
                                  <p className="text-muted small">
                                    Used when your bonus is paid separately. A flat{" "}
                                    {Math.round(config.supplemental.baseRate * 100)}% federal withholding rate is applied to the bonus
                                    amount (with a higher rate above the threshold).
                                  </p>
                                </div>
                              </div>
                            </div>
                          </>
                        ) : (
                          <div className="text-muted">
                            Enter your details and click <strong>View Results</strong> to generate your estimate.
                          </div>
                        )}
                      </div>

                      {!unlocked && (
                        <div
                          className="unk-results-overlay"
                          onClick={() => {
                            if (!hasCalculated || !results) {
                              const computed = computeResults();
                              if (!computed) return;

                              setResults(computed.results_json);
                              setHasCalculated(true);
                              latestPayloadRef.current = computed;
                            }
                            openGateFlow();
                          }}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              if (!hasCalculated || !results) {
                                const computed = computeResults();
                                if (!computed) return;

                                setResults(computed.results_json);
                                setHasCalculated(true);
                                latestPayloadRef.current = computed;
                              }
                              openGateFlow();
                            }
                          }}
                          aria-label="Results locked. Open unlock modal."
                        >
                          <div className="unk-overlay-card">
                            <div className="unk-overlay-title">Results Locked</div>
                            <div className="unk-overlay-body">
                              Enter your name and email to view your results instantly.
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center text-muted small mt-4 w-50 mx-auto">
            <p>{config.disclaimer}</p>
          </div>
        </div>
      </div>

      <LeadGateModal
        open={showLeadModal}
        onClose={() => setShowLeadModal(false)}
        onSuccess={(payload) => handleLeadSuccess(payload)}
        calculatorName={calculatorName}
        calculatorYear={String(year)}
        pageUrl={pageUrl}
        endpoint={endpoint}
        resultsJson={latestPayloadRef.current?.results_json || results}
        inputsJson={
          latestPayloadRef.current?.inputs_json || {
            lastPaycheck: Math.max(0, toNumber(lastPaycheck)),
            bonusAmount: Math.max(0, toNumber(bonusAmount)),
            taxRate: Math.max(0, toRate(taxRate)),
            year: Number(year),
            calculatorName,
          }
        }
        pdf_notes={latestPayloadRef.current?.pdf_notes}
      />

      <SuccessModal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        onViewResults={handleSuccessViewResults}
      />

      <ConfirmSendResultsModal
        open={showConfirmModal}
        email={storedEmail}
        onClose={() => setShowConfirmModal(false)}
        onViewResults={() => {
          setShowConfirmModal(false);
          scrollToResults();
        }}
        onSend={handleConfirmSend}
        onChangeEmail={handleChangeEmail}
        sending={sendingResults}
      />
         <UpdateEmailModal
           open={showUpdateEmailModal}
           initialEmail={storedEmail}
           onClose={() => setShowUpdateEmailModal(false)}
           onSend={handleUpdateEmailAndSend}
           sending={sendingResults}
           storedEmail={storedEmail}
           endpoint={PROXY_URL}
           resultsJson={latestPayloadRef.current?.results_json || results}
           inputsJson={
             latestPayloadRef.current?.inputs_json 
           }
           calculatorName={calculatorName}
           calculatorYear={String(year)}
           pageUrl={pageUrl}
           pdf_notes={latestPayloadRef.current?.pdf_notes}
         />
    </>
  );
}

export default BonusTaxCalculator;
